import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, Card, H2, H3, H4, Body, BodySm, Input, Text } from '@/components/ui';
import { NotLegalIcon } from '@/components/ui/NotLegalIcon';
import { cn } from '@/lib/utils';
import { DEMO_USER, useDemoAuth } from '@/lib/demo-auth';

interface Document {
  id: string;
  name: string;
  size: string;
  type: string;
  uploadedAt: string;
}

export function DocumentsPage() {
  const [searchParams] = useSearchParams();
  const isDemo = searchParams.get('demo') === 'true';
  const { user: demoUser, logoutDemo } = useDemoAuth();
  const [isDragging, setIsDragging] = useState(false);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [maxDocuments, setMaxDocuments] = useState(10);

  // Load demo documents or fetch from API
  useEffect(() => {
    if (isDemo) {
      setDocuments([
        { id: '1', name: 'Apartment Lease 2024.pdf', size: '2.4 MB', type: 'PDF', uploadedAt: '2 days ago' },
        { id: '2', name: 'Service Agreement.docx', size: '156 KB', type: 'DOCX', uploadedAt: '1 week ago' },
        { id: '3', name: 'Terms of Service Draft.pdf', size: '890 KB', type: 'PDF', uploadedAt: '2 weeks ago' },
      ]);
      setMaxDocuments(10);
    } else {
      setDocuments([
        { id: '1', name: 'Sample Contract.pdf', size: '1.2 MB', type: 'PDF', uploadedAt: '3 days ago' },
      ]);
      setMaxDocuments(10);
    }
  }, [isDemo, demoUser]);

  const handleUpload = () => {
    alert('File upload would be handled here');
  };

  const handleDelete = (id: string) => {
    setDocuments(prev => prev.filter(d => d.id !== id));
  };

  return (
    <div className="min-h-screen bg-surface-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-surface-200 flex flex-col">
        <div className="p-4 border-b border-surface-200">
          <Link to="/" className="flex items-center space-x-2">
            <NotLegalIcon name="document" className="w-6 h-6 text-primary-600" />
            <span className="font-bold text-surface-900">notlegal.chat</span>
            {isDemo && (
              <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full font-medium">
                Demo
              </span>
            )}
          </Link>
        </div>

        <div className="p-4 space-y-2">
          <Link to="/chat?demo=true">
            <Button variant="secondary" className="w-full" icon={<NotLegalIcon name="message" className="w-4 h-4" />}>
              Back to Chat
            </Button>
          </Link>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-2">
            <button className="w-full text-left px-4 py-3 bg-primary-100 rounded-xl text-sm font-medium text-primary-700">
              All Documents
            </button>
            <button className="w-full text-left px-4 py-3 rounded-xl hover:bg-surface-100 transition-colors text-sm text-surface-600">
              Contracts
            </button>
            <button className="w-full text-left px-4 py-3 rounded-xl hover:bg-surface-100 transition-colors text-sm text-surface-600">
              Agreements
            </button>
            <button className="w-full text-left px-4 py-3 rounded-xl hover:bg-surface-100 transition-colors text-sm text-surface-600">
              Other
            </button>
          </div>
        </div>

        <div className="p-4 border-t border-surface-200">
          {isDemo ? (
            <div className="space-y-3">
              <div className="flex items-center space-x-3 px-4 py-2">
                <img
                  src={DEMO_USER.avatarUrl}
                  alt="Demo"
                  className="w-8 h-8 rounded-full"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-surface-700 truncate">
                    {DEMO_USER.displayName}
                  </div>
                  <div className="text-xs text-surface-400 truncate">
                    {DEMO_USER.subscriptionTier} plan
                  </div>
                </div>
              </div>
              <Link to="/" onClick={logoutDemo} className="flex items-center space-x-2 px-4 py-2 text-surface-500 hover:text-red-600 transition-colors text-sm">
                <NotLegalIcon name="logout" className="w-4 h-4" />
                <span>Exit Demo</span>
              </Link>
            </div>
          ) : (
            <img
              src={DEMO_USER.avatarUrl}
              alt="User"
              className="w-8 h-8 rounded-full"
            />
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col">
        <header className="bg-white border-b border-surface-200 px-6 py-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <H3>Documents</H3>
            <BodySm className="text-surface-500">{documents.length} of {maxDocuments} documents used</BodySm>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-4xl mx-auto">
            {/* Upload Area */}
            <Card
              variant="outlined"
              className={cn(
                'mb-8 border-dashed transition-all',
                isDragging ? 'border-primary-500 bg-primary-50' : 'border-surface-300'
              )}
              padding="lg"
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
              }}
            >
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-primary-100 rounded-3xl flex items-center justify-center mx-auto mb-4">
                  <NotLegalIcon name="fileUpload" className="w-8 h-8 text-primary-600" />
                </div>
                <H4 className="mb-2">Drop files here or click to upload</H4>
                <Body className="text-surface-500 mb-4">
                  PDF, DOCX, TXT up to 10MB each
                </Body>
                <Button icon={<NotLegalIcon name="plus" className="w-4 h-4" />} onClick={handleUpload}>
                  Select Files
                </Button>
              </div>
            </Card>

            {/* Documents List */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <H4>Your Documents</H4>
                <BodySm className="text-surface-500">Click to analyze in chat</BodySm>
              </div>
              {documents.length === 0 ? (
                <Card padding="lg" className="text-center py-8">
                  <NotLegalIcon name="document" className="w-12 h-12 text-surface-300 mx-auto mb-4" />
                  <Body className="text-surface-500">No documents yet. Upload your first one!</Body>
                </Card>
              ) : (
                documents.map((doc) => (
                  <Card key={doc.id} className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-surface-100 rounded-xl flex items-center justify-center">
                        <NotLegalIcon name="document" className="w-6 h-6 text-surface-600" />
                      </div>
                      <div>
                        <div className="font-medium text-surface-900">{doc.name}</div>
                        <div className="text-sm text-surface-500">{doc.size} - Uploaded {doc.uploadedAt}</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Link to="/chat?demo=true">
                        <Button variant="ghost" size="sm" icon={<NotLegalIcon name="message" className="w-4 h-4" />}>
                          Analyze
                        </Button>
                      </Link>
                      <Button variant="ghost" size="sm" icon={<NotLegalIcon name="fileDownload" className="w-4 h-4" />}>
                        Download
                      </Button>
                      <Button variant="ghost" size="sm" icon={<NotLegalIcon name="trash" className="w-4 h-4" />} onClick={() => handleDelete(doc.id)}>
                        Delete
                      </Button>
                    </div>
                  </Card>
                ))
              )}
            </div>

            {/* Demo Mode Notice */}
            {isDemo && (
              <Card className="mt-8 bg-amber-50 border-amber-200" padding="lg">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <NotLegalIcon name="user" className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <H4 className="text-amber-800">Demo Mode Active</H4>
                    <Body className="text-amber-700 mt-1">
                      You're viewing demo data. Sign up for a real account to upload your own documents!
                    </Body>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
